import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Diamond, Shield, PiggyBank, Award, Scale, TrendingUp, MessageCircle, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getStaticVillas, getLiveVillas, getStaticSettings, getLiveSettings } from '../lib/api';
import { trackWhatsAppClick } from '../lib/analytics';
import VillaCard from '../components/VillaCard';

// Table of Contents Navigation Component for HomePage
const HomeTableOfContents = ({ sections, activeSection, language }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navHeight = 160;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - navHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav 
      className="hidden md:flex items-center justify-center gap-1 py-3 px-4 bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-[128px] md:top-[160px] z-30"
      aria-label="Page sections"
      data-testid="home-toc"
    >
      {sections.map((section, index) => (
        <React.Fragment key={section.id}>
          {index > 0 && <span className="text-gray-300 mx-1">•</span>}
          <a
            href={`#${section.id}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(section.id);
            }}
            className={`text-xs md:text-sm px-2 py-1 transition-colors whitespace-nowrap ${
              activeSection === section.id
                ? 'text-gold font-medium'
                : 'text-text-secondary hover:text-gold'
            }`}
            data-testid={`home-toc-link-${section.id}`}
          >
            {section.label[language] || section.label.en}
          </a>
        </React.Fragment>
      ))}
    </nav>
  );
};

const HomePage = () => {
  const { t, language } = useLanguage();
  const [featuredVillas, setFeaturedVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('about');
  const [siteSettings, setSiteSettings] = useState({
    youtube_url: 'https://www.youtube.com/embed/EaaCelGtdIs',
    whatsapp_number: '35795989569',
    // Home page SEO
    home_title_en: '', home_title_de: '', home_title_pl: '', home_title_sv: '', home_title_cs: '',
    home_meta_description_en: '', home_meta_description_de: '', home_meta_description_pl: '', home_meta_description_sv: '', home_meta_description_cs: '',
  });
  const liveDataFetched = useRef(false);

  // Table of Contents sections for HomePage
  const tocSections = [
    { id: 'about', label: { en: 'About', de: 'Über uns', pl: 'O nas', sv: 'Om oss', cs: 'O nás' } },
    { id: 'features', label: { en: 'Why Us', de: 'Warum wir', pl: 'Dlaczego my', sv: 'Varför vi', cs: 'Proč my' } },
    { id: 'video', label: { en: 'Video', de: 'Video', pl: 'Wideo', sv: 'Video', cs: 'Video' } },
    { id: 'villas', label: { en: 'Villas', de: 'Villen', pl: 'Wille', sv: 'Villor', cs: 'Vily' } },
    { id: 'region', label: { en: 'Dalmatia', de: 'Dalmatien', pl: 'Dalmacja', sv: 'Dalmatien', cs: 'Dalmácie' } },
    { id: 'contact', label: { en: 'Contact', de: 'Kontakt', pl: 'Kontakt', sv: 'Kontakt', cs: 'Kontakt' } },
  ];

  // Track active section for Table of Contents
  useEffect(() => {
    const handleSectionScroll = () => {
      const sections = ['about', 'features', 'video', 'villas', 'region', 'contact'];
      const navHeight = 200;
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= navHeight && rect.bottom > navHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleSectionScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleSectionScroll);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  // SWR Pattern: Static first, then live API in background
  const loadData = async () => {
    // Step 1: Load static data immediately
    const [staticVillas, staticSettings] = await Promise.all([
      getStaticVillas(),
      getStaticSettings()
    ]);
    
    if (staticVillas && staticVillas.length > 0) {
      const featured = staticVillas.filter(v => v.is_featured);
      setFeaturedVillas(featured.length > 0 ? featured.slice(0, 3) : staticVillas.slice(0, 3));
      setLoading(false);
    }
    
    if (staticSettings) {
      setSiteSettings(prev => ({
        ...prev,
        youtube_url: staticSettings.youtube_url || prev.youtube_url,
        whatsapp_number: staticSettings.whatsapp_number || prev.whatsapp_number,
        home_title_en: staticSettings.home_title_en || '',
        home_title_de: staticSettings.home_title_de || '',
        home_title_pl: staticSettings.home_title_pl || '',
        home_title_sv: staticSettings.home_title_sv || '',
        home_title_cs: staticSettings.home_title_cs || '',
        home_meta_description_en: staticSettings.home_meta_description_en || '',
        home_meta_description_de: staticSettings.home_meta_description_de || '',
        home_meta_description_pl: staticSettings.home_meta_description_pl || '',
        home_meta_description_sv: staticSettings.home_meta_description_sv || '',
        home_meta_description_cs: staticSettings.home_meta_description_cs || '',
      }));
    }
    
    // Step 2: Fetch live data in background (only once)
    if (!liveDataFetched.current) {
      liveDataFetched.current = true;
      
      const [liveVillas, liveSettings] = await Promise.all([
        getLiveVillas(),
        getLiveSettings()
      ]);
      
      if (liveVillas && liveVillas.length > 0) {
        const featured = liveVillas.filter(v => v.is_featured);
        setFeaturedVillas(featured.length > 0 ? featured.slice(0, 3) : liveVillas.slice(0, 3));
      }
      
      if (liveSettings) {
        setSiteSettings(prev => ({
          ...prev,
          youtube_url: liveSettings.youtube_url || prev.youtube_url,
          whatsapp_number: liveSettings.whatsapp_number || prev.whatsapp_number,
          home_title_en: liveSettings.home_title_en || prev.home_title_en,
          home_title_de: liveSettings.home_title_de || prev.home_title_de,
          home_title_pl: liveSettings.home_title_pl || prev.home_title_pl,
          home_title_sv: liveSettings.home_title_sv || prev.home_title_sv,
          home_title_cs: liveSettings.home_title_cs || prev.home_title_cs,
          home_meta_description_en: liveSettings.home_meta_description_en || prev.home_meta_description_en,
          home_meta_description_de: liveSettings.home_meta_description_de || prev.home_meta_description_de,
          home_meta_description_pl: liveSettings.home_meta_description_pl || prev.home_meta_description_pl,
          home_meta_description_sv: liveSettings.home_meta_description_sv || prev.home_meta_description_sv,
          home_meta_description_cs: liveSettings.home_meta_description_cs || prev.home_meta_description_cs,
        }));
      }
      
      setLoading(false);
    }
  };

  // Convert YouTube watch URL to embed URL and extract video ID
  const getEmbedUrl = (url) => {
    if (!url) return 'https://www.youtube-nocookie.com/embed/EaaCelGtdIs';
    if (url.includes('/embed/')) return url.replace('youtube.com', 'youtube-nocookie.com');
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (match) {
      return `https://www.youtube-nocookie.com/embed/${match[1]}`;
    }
    return url;
  };

  // Extract YouTube video ID for thumbnail
  const getYouTubeVideoId = (url) => {
    if (!url) return 'EaaCelGtdIs';
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/)([^&\s?]+)/);
    return match ? match[1] : 'EaaCelGtdIs';
  };

  const WHATSAPP_NUMBER = siteSettings.whatsapp_number;
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Helper to get localized URL
  const getLocalizedUrl = (path) => {
    if (language === 'en') return path;
    return `/${language}${path}`;
  };

  const openWhatsApp = (location = 'homepage') => {
    // Track WhatsApp click conversion
    trackWhatsAppClick(location);
    
    const message = language === 'ru' 
      ? 'Здравствуйте! Меня интересуют виллы на первой линии от моря в Хорватии.'
      : 'Hello! I am interested in first line villas in Croatia.';
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const features = [
    { 
      icon: Diamond, 
      title: t('features.bestOffers'), 
      desc: t('features.bestOffersDesc')
    },
    { 
      icon: Award, 
      title: t('features.quality'), 
      desc: t('features.qualityDesc')
    },
    { 
      icon: PiggyBank, 
      title: t('features.saveMoney'), 
      desc: t('features.saveMoneyDesc')
    },
    { 
      icon: Shield, 
      title: t('features.bestPrice'), 
      desc: t('features.bestPriceDesc')
    },
    { 
      icon: Scale, 
      title: t('features.legalSupport'), 
      desc: t('features.legalSupportDesc')
    },
    { 
      icon: TrendingUp, 
      title: t('features.businessGain'), 
      desc: t('features.businessGainDesc')
    },
  ];

  // Get current canonical URL based on language (with trailing slash for consistency)
  const getCanonicalUrl = () => {
    if (language === 'en') return 'https://villa.ooo/';
    return `https://villa.ooo/${language}/`;
  };

  // Supported languages for hreflang
  const hreflangLanguages = ['en', 'de', 'pl', 'sv', 'cs'];

  // Get og:locale based on language
  const getOgLocale = () => {
    const locales = { en: 'en_US', de: 'de_DE', pl: 'pl_PL', sv: 'sv_SE', cs: 'cs_CZ' };
    return locales[language] || 'en_US';
  };

  // Get home page title from settings or fallback to translations
  const getHomeTitle = () => {
    const settingsTitle = siteSettings[`home_title_${language}`];
    if (settingsTitle && settingsTitle.trim()) return settingsTitle;
    return String(t('seo.homeTitle'));
  };

  // Get home page meta description from settings or fallback to translations
  const getHomeMetaDescription = () => {
    const settingsDesc = siteSettings[`home_meta_description_${language}`];
    if (settingsDesc && settingsDesc.trim()) return settingsDesc;
    return String(t('seo.homeDescription'));
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{getHomeTitle()}</title>
        <meta name="description" content={getHomeMetaDescription()} />
        <meta property="og:title" content={getHomeTitle()} />
        <meta property="og:description" content={getHomeMetaDescription()} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Villa.ooo" />
        <meta property="og:locale" content={getOgLocale()} />
        <meta property="og:image" content="https://villa.ooo/hero-villa.jpg" />
        <meta property="og:url" content={getCanonicalUrl()} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl()} />
        {/* hreflang for all languages */}
        {hreflangLanguages.map(lang => (
          <link 
            key={lang} 
            rel="alternate" 
            hrefLang={lang} 
            href={lang === 'en' ? 'https://villa.ooo/' : `https://villa.ooo/${lang}/`} 
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href="https://villa.ooo/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Villa.ooo",
            "description": String(t('seo.homeDescription')),
            "url": "https://villa.ooo",
            "telephone": `+${WHATSAPP_NUMBER}`,
            "areaServed": ["Dalmatia", "Croatia", "Trogir", "Split", "Primosten", "Omis", "Makarska"],
            "priceRange": "€€€€",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Dalmatia",
              "addressCountry": "HR"
            },
            "sameAs": [
              `https://wa.me/${WHATSAPP_NUMBER}`
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Villa.ooo",
            "url": "https://villa.ooo",
            "logo": "https://villa.ooo/logo-villa.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": `+${WHATSAPP_NUMBER}`,
              "contactType": "sales",
              "availableLanguage": ["English", "German", "Polish", "Swedish", "Czech", "Croatian"]
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-screen flex items-end" data-testid="hero-section">
        {/* Background Image - User uploaded image */}
        <div className="absolute inset-0">
          <img
            src="/hero-villa.jpg"
            alt={t('hero.imageAlt')}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="label-style text-gold mb-4">{t('hero.subtitle')}</p>
            <h1 className="heading-hero font-heading text-white mb-6 max-w-4xl">
              {t('hero.title')}
            </h1>
            <p className="text-white/70 text-lg md:text-xl max-w-xl mb-10">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={getLocalizedUrl('/catalog')}
                className="btn-primary px-8 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3"
                data-testid="hero-cta-catalog"
              >
                {t('hero.cta')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={() => openWhatsApp('hero')}
                className="btn-whatsapp px-8 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3 bg-[#25D366] text-white border border-[#25D366] hover:bg-[#128C7E] transition-all"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                {t('hero.ctaSecondary')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents Navigation */}
      <HomeTableOfContents 
        sections={tocSections} 
        activeSection={activeSection} 
        language={language} 
      />

      {/* About Section */}
      <section id="about" className="section-padding" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="aspect-[4/5] lg:aspect-auto lg:h-[600px] overflow-hidden"
            >
              <img
                src="/about-bg.jpg"
                alt={t('images.aboutAlt')}
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="label-style mb-4">{t('about.label')}</p>
              <h2 className="heading-section font-heading text-night mb-8">
                {t('about.title')}
              </h2>
              <p className="text-text-secondary text-lg leading-relaxed mb-8">
                {t('about.description')}
              </p>
              
              <p className="text-text-secondary leading-relaxed mb-12">
                {t('about.details')}
              </p>

              <button
                onClick={() => openWhatsApp('about')}
                className="btn-primary px-8 py-4 text-sm tracking-widest uppercase inline-flex items-center gap-3"
                data-testid="about-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                {t('about.ctaButton')}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section id="features" className="section-padding bg-night" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-style text-gold mb-4">{t('about.label')}</p>
            <h2 className="heading-section font-heading text-white">{t('featuresSection.title')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 border border-white/10 hover:border-gold/50 transition-colors"
              >
                <feature.icon className="w-10 h-10 text-gold mb-6" />
                <h3 className="font-heading text-xl text-white mb-3">{feature.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Presentation Section */}
      <section id="video" className="section-padding bg-bone" data-testid="video-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-style mb-4">{t('video.title')}</p>
            <h2 className="heading-section font-heading text-night">
              {t('video.subtitle')}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative aspect-video max-w-4xl mx-auto overflow-hidden rounded-lg shadow-2xl"
          >
            {!videoLoaded ? (
              /* Lite YouTube Embed - показываем превью до клика */
              <button
                onClick={() => setVideoLoaded(true)}
                className="absolute inset-0 w-full h-full cursor-pointer group"
                aria-label={t('video.subtitle')}
                data-testid="video-thumbnail"
              >
                {/* YouTube thumbnail */}
                <img
                  src={`https://i.ytimg.com/vi/${getYouTubeVideoId(siteSettings.youtube_url)}/maxresdefault.jpg`}
                  alt={t('images.videoAlt')}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                </div>
              </button>
            ) : (
              /* Загружаем iframe только после клика */
              <iframe
                src={`${getEmbedUrl(siteSettings.youtube_url)}?rel=0&modestbranding=1&autoplay=1&hl=${language}`}
                title={t('images.videoAlt')}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="video-iframe"
              />
            )}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center text-text-secondary mt-8 max-w-2xl mx-auto"
          >
            {t('video.description')}
          </motion.p>
        </div>
      </section>

      {/* Featured Villas Section */}
      <section id="villas" className="section-padding" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
          >
            <div>
              <p className="label-style mb-4">{t('villas.label')}</p>
              <h2 className="heading-section font-heading text-night">{t('villas.title')}</h2>
            </div>
            <Link
              to={getLocalizedUrl('/catalog')}
              className="text-gold hover:text-night transition-colors flex items-center gap-2 text-sm tracking-wide"
              data-testid="featured-view-all"
            >
              {t('villas.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200 mb-6" />
                  <div className="h-4 bg-gray-200 w-1/3 mb-3" />
                  <div className="h-8 bg-gray-200 w-2/3 mb-3" />
                  <div className="h-4 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredVillas.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredVillas.map((villa, index) => (
                <VillaCard key={villa.id} villa={villa} index={index} />
              ))}
            </div>
          ) : (
            <p className="text-center text-text-secondary py-16">{t('villas.noVillas')}</p>
          )}
        </div>
      </section>

      {/* Dalmatia Section */}
      <section id="region" className="section-padding" data-testid="dalmatia-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="label-style mb-4">{t('dalmatia.region')}</p>
              <h2 className="heading-section font-heading text-night mb-8">
                {t('dalmatia.title')}
              </h2>
              <p className="text-text-secondary text-lg leading-relaxed mb-6">
                {t('dalmatia.description')}
              </p>
              <p className="text-text-secondary leading-relaxed">
                {t('dalmatia.fullDescription')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="relative"
            >
              <div className="image-hover aspect-[4/3] rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="https://res.cloudinary.com/dfww0gvcr/image/upload/v1775686991/Luxury_villa_Omis_region_04_jsk487.png"
                  alt={t('images.dalmatiaAlt')}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-gold text-night px-6 py-3 font-heading text-lg">
                {t('images.omisLabel')}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="section-padding bg-night" data-testid="cta-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="heading-section font-heading text-white mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
              {t('cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => openWhatsApp('cta')}
                className="bg-[#25D366] text-white px-10 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3 hover:bg-[#128C7E] transition-colors"
                data-testid="cta-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                {t('cta.whatsapp')}
              </button>
              <Link
                to={getLocalizedUrl('/contact')}
                className="btn-primary px-10 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3"
                data-testid="cta-contact-btn"
              >
                {t('cta.contact')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <button
        onClick={() => openWhatsApp('floating')}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#128C7E] transition-colors hover:scale-110"
        data-testid="floating-whatsapp-btn"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </button>
    </>
  );
};

export default HomePage;
